import useSWR from 'swr';
import type {
  RoutingGraphImportRunStatusViewModel,
  RoutingGraphImportStatusTone,
  RoutingGraphServingStatusViewModel,
} from '@workspace/silk-ui';

import { useSession } from '@/context/SessionContext';
import {
  getRoutingGraphImportStatus,
  type RoutingGraphImportRun,
  type RoutingGraphImportStatus,
  type RoutingGraphServingStatus,
} from '@/lib/routing-graph-import-status';
import type { SessionProfile } from '@/types';

export const ROUTING_GRAPH_ACTIVE_POLL_INTERVAL_MS = 5_000;
export const ROUTING_GRAPH_IDLE_POLL_INTERVAL_MS = 30_000;

const ACTIVE_STATES = new Set(['queued', 'running', 'promoting', 'cancel_requested', 'stalled']);
const PLATFORM_PERMISSIONS = new Set(['platform.manage', 'platform.mapping.manage']);
const normalize = (value: string) => value.trim().toLowerCase();

export function hasPlatformRoutingPermission(session?: SessionProfile | null): boolean {
  const permissions = [...(session?.scopes ?? []), ...(session?.permissionSummary?.global ?? [])];
  return permissions.some(permission => PLATFORM_PERMISSIONS.has(normalize(permission)));
}

export function isActiveRoutingGraphRun(run?: RoutingGraphImportRun | null): boolean {
  return Boolean(run && (run.stalled || ACTIVE_STATES.has(normalize(run.status ?? ''))));
}

export function hasActiveRoutingGraphRun(status?: RoutingGraphImportStatus): boolean {
  if (!status) return false;
  return (status.recent_runs ?? []).some(isActiveRoutingGraphRun)
    || (status.regions ?? []).some(region => isActiveRoutingGraphRun(region.last_run));
}

export function routingGraphStatusPollInterval(status?: RoutingGraphImportStatus): number {
  return hasActiveRoutingGraphRun(status)
    ? ROUTING_GRAPH_ACTIVE_POLL_INTERVAL_MS
    : ROUTING_GRAPH_IDLE_POLL_INTERVAL_MS;
}

export function heartbeatAgeSeconds(timestamp?: string | null, nowMs = Date.now()): number | null {
  if (!timestamp) return null;
  const heartbeatMs = Date.parse(timestamp);
  if (!Number.isFinite(heartbeatMs)) return null;
  return Math.max(0, Math.floor((nowMs - heartbeatMs) / 1_000));
}

export type RoutingGraphImportRunViewModel = RoutingGraphImportRunStatusViewModel;

export interface RoutingGraphImportStatusViewModel {
  visible: boolean;
  loading: boolean;
  refreshing: boolean;
  stale: boolean;
  error: string | null;
  runs: RoutingGraphImportRunViewModel[];
  servingGraph: RoutingGraphServingStatusViewModel | null;
  refresh: () => Promise<RoutingGraphImportStatus | undefined>;
}

interface RoutingGraphImportStatusViewModelInput {
  visible: boolean;
  sessionLoading?: boolean;
  data?: RoutingGraphImportStatus;
  error?: unknown;
  loading?: boolean;
  refreshing?: boolean;
  refresh: () => Promise<RoutingGraphImportStatus | undefined>;
  nowMs?: number;
}

export function buildRoutingGraphImportRunViewModel(
  run: RoutingGraphImportRun,
  nowMs = Date.now(),
): RoutingGraphImportRunViewModel {
  const status = normalize(run.status ?? 'unknown');
  const tone: RoutingGraphImportStatusTone = status === 'succeeded'
    ? 'success'
    : status === 'failed' || status === 'cancelled'
      ? 'danger'
      : status === 'stalled' || status === 'cancel_requested'
        ? 'warning'
        : status === 'running' || status === 'promoting'
          ? 'info'
          : 'neutral';
  return {
    id: run.id,
    regionKey: run.region_key || 'Unknown region',
    statusLabel: status.replaceAll('_', ' '),
    statusTone: tone,
    phaseLabel: (run.phase || 'Pending').replaceAll('_', ' '),
    progressPercent: run.progress_percent ?? null,
    progressMessage: run.progress_message,
    heartbeatAgeSeconds: heartbeatAgeSeconds(run.last_heartbeat_at ?? run.heartbeat_at, nowMs),
    rowsPerSecond: run.rows_per_second ?? run.throughput_per_second ?? null,
    currentBatch: run.current_batch ?? null,
    totalBatches: run.total_batches ?? null,
    error: run.error,
    active: isActiveRoutingGraphRun(run),
  };
}

function buildServingGraphViewModel(serving?: RoutingGraphServingStatus | null): RoutingGraphServingStatusViewModel | null {
  if (!serving) return null;
  const promoted = serving.promoted_at ? new Date(serving.promoted_at) : null;
  return {
    graphVersion: serving.graph_version || 'Unavailable',
    regionsLabel: serving.region_keys?.join(', ') || `${serving.members?.length ?? 0} regions`,
    promotedLabel: promoted && Number.isFinite(promoted.getTime()) ? promoted.toLocaleString() : 'Not reported',
    storageModeLabel: (serving.storage_mode || 'unknown').replaceAll('_', ' '),
  };
}

export function buildRoutingGraphImportStatusViewModel({
  visible,
  sessionLoading = false,
  data,
  error,
  loading = false,
  refreshing = false,
  refresh,
  nowMs = Date.now(),
}: RoutingGraphImportStatusViewModelInput): RoutingGraphImportStatusViewModel {
  const runsByID = new Map<string, RoutingGraphImportRun>();
  for (const run of data?.recent_runs ?? []) runsByID.set(run.id, run);
  for (const region of data?.regions ?? []) {
    if (region.last_run && !runsByID.has(region.last_run.id)) runsByID.set(region.last_run.id, region.last_run);
  }

  return {
    visible,
    loading: sessionLoading || (visible && loading),
    refreshing: visible && refreshing,
    stale: Boolean(data && error),
    error: error instanceof Error ? error.message : error ? String(error) : null,
    runs: [...runsByID.values()].map(run => buildRoutingGraphImportRunViewModel(run, nowMs)),
    servingGraph: buildServingGraphViewModel(data?.serving_graph),
    refresh,
  };
}

export function useRoutingGraphImportStatusController(): RoutingGraphImportStatusViewModel {
  const { session, loading: sessionLoading } = useSession();
  const visible = hasPlatformRoutingPermission(session);
  const { data, error, isLoading, isValidating, mutate } = useSWR<RoutingGraphImportStatus>(
    visible ? 'portal-routing-graph-import-status' : null,
    getRoutingGraphImportStatus,
    {
      refreshInterval: routingGraphStatusPollInterval,
      revalidateOnFocus: true,
      keepPreviousData: true,
    },
  );

  return buildRoutingGraphImportStatusViewModel({
    visible,
    sessionLoading,
    data,
    error,
    loading: isLoading,
    refreshing: isValidating,
    refresh: async () => mutate(),
  });
}
